import { useClassNameMapper } from 'react-bootstrap';
import {
  FeaturedGuideWidgetLayout,
  FeaturedGuideWidgetListStyle,
  LeadWithOption,
  type LayoutProps,
  type ListLayoutProps
} from '../FeaturedGuideWidget/types';
import React, { useEffect } from 'react';
import { EndUserComponent } from '@aurora/shared-types/pages/enums';
import useTranslation from '../../../useTranslation';
import type { QueryResult } from '@apollo/client/react';
import type {
  FeaturedGuideWidgetQuery,
  FeaturedGuideWidgetQueryVariables,
  GuideViewFragment
} from '../../../../types/graphql-types';
import useFeaturedGuideWidget from '../FeaturedGuideWidget/useFeaturedGuideWidget';
import FeaturedWidgetPlaceholder from '../../FeaturedWidgetPlaceholder/FeaturedWidgetPlaceholder';
import PaneledItemList from '../../../common/List/PaneledItemList/PaneledItemList';
import { GuideViewVariant, ItemType } from '../../../../types/enums';
import type { ItemViewTypeAndProps } from '../../../entities/types';
import { type GuideConnection } from '@aurora/shared-generated/types/graphql-schema-types';
import type { GridListTypeAndProps } from '@aurora/shared-client/components/common/List';
import { PanelType } from '@aurora/shared-client/components/common/Panel/enums';
import { PagerVariant } from '@aurora/shared-client/components/common/Pager/enums';
import { ListItemSpacing, ListVariant } from '@aurora/shared-client/components/common/List/enums';
import getGridListColumns from '../../../../helpers/util/GridListColumnsHelper';
import { isLayoutPropsCardLayout } from '../FeaturedGuideWidgetEditor/useFeaturedGuideWidgetconfiguration';

/**
 * Transforms the settings from the end user widget configuration for use by the GuideView components
 *
 * @param layoutProps the FeaturedGuideWidget message Layout props
 */
export function getItemViewTypeAndProps(
  layoutProps: LayoutProps
): ItemViewTypeAndProps<ItemType.GUIDE, GuideViewVariant> {
  const {
    layout,
    descriptionClampLines,
    layoutOptions: { useDescription }
  } = layoutProps;
  if (layout === FeaturedGuideWidgetLayout.LIST) {
    const { leadWithOption } = layoutProps as ListLayoutProps;
    return {
      type: GuideViewVariant.INLINE,
      props: {
        useDescription: useDescription,
        clampDescriptionLines: descriptionClampLines,
        useIcon: leadWithOption === LeadWithOption.ICON,
        useAvatar: leadWithOption ? leadWithOption === LeadWithOption.AVATAR : true
      }
    };
  }

  const {
    cardSize,
    textAlignment,
    layoutOptions: { useAvatar }
  } = layoutProps;

  return {
    type: GuideViewVariant.CARD,
    props: {
      useDescription: useDescription,
      clampDescriptionLines: descriptionClampLines,
      useAvatar: useAvatar,
      cardSize,
      textAlignment
    }
  };
}

/**
 * Determines which Panel should be used by the PaneledItemList
 *
 * @param hasGuides whether the component has any guide
 * @param layoutProps the FeaturedGuidesWidget Layout props
 */
function getPanelType(hasGuides: boolean, layoutProps: LayoutProps): PanelType {
  if (!hasGuides) {
    return PanelType.NONE;
  } else if (isLayoutPropsCardLayout(layoutProps)) {
    return PanelType.SPACED;
  } else {
    const ListStyleToPanelTypeMap: Record<FeaturedGuideWidgetListStyle, PanelType> = {
      [FeaturedGuideWidgetListStyle.SPACE]: PanelType.STANDARD,
      [FeaturedGuideWidgetListStyle.DIVIDE]: PanelType.DIVIDER,
      [FeaturedGuideWidgetListStyle.BORDER]: PanelType.BUBBLE
    };

    return ListStyleToPanelTypeMap[layoutProps.listStyle];
  }
}

interface Props {
  /**
   * Variables for the FeaturedGuideWidget query
   */
  guideQueryVariables: FeaturedGuideWidgetQueryVariables;
  /**
   * Callback function used to lift the query result up to the parent widget
   */
  onGuideQueryUpdate: (
    queryResult: QueryResult<FeaturedGuideWidgetQuery, FeaturedGuideWidgetQueryVariables>
  ) => void;
  /**
   * Callback function that toggles the visibility of the AddFeaturedGuideModal
   */
  toggleModal: () => void;
  /**
   * The configuration for the PaneledItemList header
   */
  header: { title: string; titleSrOnly: boolean };
  /**
   * Layout selection props
   */
  layoutProps: LayoutProps;
  /**
   * Whether to display the pager or not
   */
  showPager: boolean;
  /**
   * Class name(s) to apply to the featured guides list
   */
  className: string;
}

/**
 * Renders the list of featured guides
 *
 * @author Amit Agrawal
 */
const FeaturedGuidesList: React.FC<React.PropsWithChildren<Props>> = ({
  guideQueryVariables,
  onGuideQueryUpdate,
  toggleModal,
  header,
  layoutProps,
  showPager,
  className
}) => {
  const cx = useClassNameMapper();
  const i18n = useTranslation(EndUserComponent.FEATURED_GUIDES_LIST);
  const { formatMessage, loading: textLoading } = i18n;

  const { first: pageSize } = guideQueryVariables;

  const guidesQueryResult: QueryResult<
    FeaturedGuideWidgetQuery,
    FeaturedGuideWidgetQueryVariables
  > = useFeaturedGuideWidget(guideQueryVariables);

  useEffect(() => {
    onGuideQueryUpdate(guidesQueryResult);
  }, [guidesQueryResult, onGuideQueryUpdate]);

  if (textLoading || guidesQueryResult.loading) {
    return null;
  }

  /**
   * Renders the empty state for the FeaturedGuideWidget
   */
  const EmptyState: React.FC<React.PropsWithChildren<unknown>> = () => (
    <FeaturedWidgetPlaceholder buttonText={formatMessage('edit')} onClick={toggleModal} />
  );

  const containsGuides: boolean =
    guidesQueryResult?.data?.featuredGuideWidget?.guides?.edges?.length > 0;

  const { title, titleSrOnly } = header;

  const cardLayoutListVariant: GridListTypeAndProps<GuideViewFragment> = {
    type: ListVariant.GRID,
    props: {
      itemSpacing: ListItemSpacing.LG,
      colProps: getGridListColumns(pageSize)
    }
  };

  /**
   * Renders the header for the list.
   */
  function renderHeader(): React.ReactElement {
    return containsGuides && <h3 className={cx('text-break h5 mb-0')}>{title}</h3>;
  }

  return (
    <PaneledItemList<
      GuideViewFragment,
      ItemType.GUIDE,
      ItemViewTypeAndProps<ItemType.GUIDE, GuideViewVariant>,
      FeaturedGuideWidgetQuery,
      FeaturedGuideWidgetQueryVariables
    >
      id="FeaturedGuides"
      queryResult={guidesQueryResult}
      itemPath="featuredGuideWidget.guides"
      onUpdate={(guideConnection: GuideConnection): FeaturedGuideWidgetQuery => {
        return {
          featuredGuideWidget: {
            guides: guideConnection
          }
        };
      }}
      pageSize={pageSize}
      pagerVariant={{
        type: PagerVariant.LOAD_MORE,
        props: {
          title: formatMessage('loadMore')
        }
      }}
      type={ItemType.GUIDE}
      variant={getItemViewTypeAndProps(layoutProps)}
      useEmpty
      empty={EmptyState}
      panel={getPanelType(containsGuides, layoutProps)}
      listVariant={isLayoutPropsCardLayout(layoutProps) ? cardLayoutListVariant : undefined}
      header={renderHeader}
      className={cx(className)}
      isHeaderSrOnly={titleSrOnly}
      useFooter={showPager}
      footerClassName={cx({ 'bg-transparent lia-g-mt-5': isLayoutPropsCardLayout(layoutProps) })}
    />
  );
};

export default FeaturedGuidesList;
